import React, { Component } from "react";
import PropTypes from "prop-types";
import { GenericChartComponent } from "@chartLib";
import { useThemeStore } from "../../store/themeStore";
import { isDarkTheme } from "../../config/themes";

// Global registry for resize handlers - allows EventCapture to trigger resize from touch
if (!window.__chartResizeHandlers) {
  window.__chartResizeHandlers = new Map();
}

/**
 * ChartResizeHandle - Visual design matching PrimeReact Splitter
 *
 * STATUS: ✅ Funcionamento completo e design atualizado
 * DESIGN: ✅ Cores, dimensões e comportamento idênticos ao PrimeReact
 *
 * Características:
 * - Cor: #424b57 (rgb(66, 75, 87)) - exata do PrimeReact
 * - Dimensões: 24px × 4px (largura × altura)
 * - Background gutter: rgba(255, 255, 255, 0.03)
 * - Sem border-radius, sem box-shadow
 * - Transition: opacity 0.15s ease (consistent across all elements)
 * - Cursor: ns-resize
 */
class ChartResizeHandle extends Component {
  constructor(props) {
    super(props);

    this.state = {
      isDragging: false,
      isHovered: false,
    };

    // Bind methods - using Pointer Events for unified mouse/touch support
    this.handlePointerDown = this.handlePointerDown.bind(this);
    this.handlePointerMove = this.handlePointerMove.bind(this);
    this.handlePointerUp = this.handlePointerUp.bind(this);
    this.handlePointerEnter = this.handlePointerEnter.bind(this);
    this.handlePointerLeave = this.handlePointerLeave.bind(this);
    // Touch event handlers to prevent EventCapture from intercepting
    this.handleTouchStart = this.handleTouchStart.bind(this);
    this.handleTouchMove = this.handleTouchMove.bind(this);
    this.handleTouchEnd = this.handleTouchEnd.bind(this);
    // Touch drag handlers for document-level tracking
    this.handleTouchDrag = this.handleTouchDrag.bind(this);
    this.handleTouchDragEnd = this.handleTouchDragEnd.bind(this);
    this.renderSVG = this.renderSVG.bind(this);

    // Drag state - simplified
    this.startY = 0;
    this.activePointerId = null; // Track which pointer is active
    this.isTouch = false; // Track if current interaction is touch (to avoid double event handling)
  }

  // Touch event handlers - stop propagation to prevent EventCapture from panning
  handleTouchStart(e) {
    if (this.props.disabled) return;
    // Set global flag immediately so EventCapture knows to skip
    window.chartResizeInProgress = true;
    e.stopPropagation();
    // Note: preventDefault may not work in passive listener, but pointer events handle the logic
  }

  handleTouchMove(e) {
    if (this.state.isDragging) {
      e.stopPropagation();
    }
  }

  handleTouchEnd(e) {
    if (this.state.isDragging) {
      e.stopPropagation();
    }
    // Don't clear the flag here - let pointer events handle cleanup
  }

  // Document-level touch handlers for tracking drag (used when pointer events don't work)
  handleTouchDrag(e) {
    if (!this.state.isDragging || this.props.disabled) return;
    if (!e.touches || e.touches.length === 0) return;

    e.preventDefault();

    const currentWindowY =
      e.touches[0].clientY + (this._dragView ?? window).scrollY;
    const windowDeltaY = currentWindowY - this.startWindowY;

    if (this.props.onResize) {
      this.props.onResize(
        this.props.sectionIndex,
        windowDeltaY,
        currentWindowY,
      );
    }
  }

  handleTouchDragEnd(e) {
    if (!this.state.isDragging) return;

    // Clear global resize flag
    window.chartResizeInProgress = false;

    this.activePointerId = null;
    this.isTouch = false;
    this.setState({ isDragging: false, isHovered: false });

    // Remove touch listeners (must match add options) from the drag's window
    const dragView = this._dragView ?? window;
    dragView.removeEventListener("touchmove", this.handleTouchDrag, {
      passive: false,
      capture: true,
    });
    dragView.removeEventListener("touchend", this.handleTouchDragEnd, {
      capture: true,
    });
    dragView.removeEventListener("touchcancel", this.handleTouchDragEnd, {
      capture: true,
    });
    this._dragDoc = null;
    this._dragView = null;

    // Notify parent of end
    if (this.props.onEndResize) {
      this.props.onEndResize(this.props.sectionIndex);
    }
  }

  handlePointerEnter() {
    if (!this.props.disabled) {
      this.setState({ isHovered: true });
    }
  }

  handlePointerLeave() {
    if (!this.state.isDragging) {
      this.setState({ isHovered: false });
    }
  }

  handlePointerDown(e) {
    if (this.props.disabled) {
      return;
    }

    e.preventDefault();
    e.stopPropagation();

    // Set global flag to prevent EventCapture from panning during resize
    window.chartResizeInProgress = true;

    // Track if this is a touch interaction to avoid double event handling
    this.isTouch = e.pointerType === "touch";

    // Capture the pointer for this element
    if (e.target.setPointerCapture) {
      e.target.setPointerCapture(e.pointerId);
    }
    this.activePointerId = e.pointerId;

    // Store the original height for consistent positioning during drag
    this.originalHeight = null;

    // PoC #popout: the handle may live in a popout window — the drag's
    // move/up/cancel land on THAT window's document, not the main one.
    this._dragDoc = e.target?.ownerDocument ?? document;
    this._dragView = this._dragDoc.defaultView ?? window;

    // Use window coordinates for absolute positioning
    const pointerWindowY = e.clientY + this._dragView.scrollY;
    const pointerClientY = e.clientY;

    this.startWindowY = pointerWindowY;
    this.startClientY = pointerClientY;

    this.setState({ isDragging: true });

    // For touch: only use touch event listeners (pointer events from touch on SVG don't
    // reliably fire pointermove on document when capture is set)
    // For mouse: only use pointer event listeners
    if (this.isTouch) {
      // Use window (not document) and capture phase to ensure we get all touch events
      // even when touch moves outside the original element
      this._dragView.addEventListener("touchmove", this.handleTouchDrag, {
        passive: false,
        capture: true,
      });
      this._dragView.addEventListener("touchend", this.handleTouchDragEnd, {
        capture: true,
      });
      this._dragView.addEventListener("touchcancel", this.handleTouchDragEnd, {
        capture: true,
      });
    } else {
      this._dragDoc.body.style.cursor = "ns-resize";
      this._dragDoc.addEventListener("pointermove", this.handlePointerMove);
      this._dragDoc.addEventListener("pointerup", this.handlePointerUp);
      this._dragDoc.addEventListener("pointercancel", this.handlePointerUp);
    }

    // Notify parent of start with window coordinates
    if (this.props.onStartResize) {
      this.props.onStartResize(this.props.sectionIndex, pointerWindowY);
    }
  }

  handlePointerMove(e) {
    if (!this.state.isDragging || this.props.disabled) return;

    // Only respond to the captured pointer
    if (e.pointerId !== this.activePointerId) return;

    e.preventDefault();

    // Calculate delta using window coordinates
    const currentWindowY = e.clientY + (this._dragView ?? window).scrollY;

    // Calculate delta from window coordinates (absolute positioning)
    const windowDeltaY = currentWindowY - this.startWindowY;

    // Use window delta for calculations (more stable)
    if (this.props.onResize) {
      this.props.onResize(
        this.props.sectionIndex,
        windowDeltaY,
        currentWindowY,
      );
    }
  }

  handlePointerUp(e) {
    if (!this.state.isDragging) return;

    // Only respond to the captured pointer
    if (e.pointerId !== this.activePointerId) return;

    e.preventDefault();

    // Clear global resize flag
    window.chartResizeInProgress = false;

    // Release pointer capture
    if (e.target.releasePointerCapture) {
      try {
        e.target.releasePointerCapture(e.pointerId);
      } catch {
        // Ignore if capture was already released
      }
    }
    this.activePointerId = null;
    this.isTouch = false;

    this.setState({ isDragging: false, isHovered: false });

    // Reset cursor + remove listeners on the document the drag ran in
    const dragDoc = this._dragDoc ?? document;
    dragDoc.body.style.cursor = "";
    dragDoc.removeEventListener("pointermove", this.handlePointerMove);
    dragDoc.removeEventListener("pointerup", this.handlePointerUp);
    dragDoc.removeEventListener("pointercancel", this.handlePointerUp);
    this._dragDoc = null;
    this._dragView = null;

    // Notify parent of end
    if (this.props.onEndResize) {
      this.props.onEndResize(this.props.sectionIndex);
    }
  }

  getRegistryKey() {
    const { chartId, sectionIndex } = this.props;
    return chartId ? `${chartId}_${sectionIndex}` : sectionIndex;
  }

  componentDidMount() {
    // Register this resize handle for touch event routing from EventCapture
    const registryKey = this.getRegistryKey();
    if (window.__chartResizeHandlers) {
      window.__chartResizeHandlers.set(registryKey, {
        onStart: (y) => {
          if (this.props.disabled) return false;
          window.chartResizeInProgress = true;
          this.startWindowY = y;
          this.startClientY = y;
          this.setState({ isDragging: true });
          if (this.props.onStartResize) {
            this.props.onStartResize(sectionIndex, y);
          }
          // Add global listeners for tracking (use window with capture for reliable touch handling)
          window.addEventListener("touchmove", this.handleGlobalTouchMove, {
            passive: false,
            capture: true,
          });
          window.addEventListener("touchend", this.handleGlobalTouchEnd, {
            capture: true,
          });
          return true;
        },
        onMove: (y) => {
          if (!this.state.isDragging) return;
          const delta = y - this.startWindowY;
          if (this.props.onResize) {
            this.props.onResize(sectionIndex, delta, y);
          }
        },
        onEnd: () => {
          if (!this.state.isDragging) return;
          window.chartResizeInProgress = false;
          this.setState({ isDragging: false, isHovered: false });
          window.removeEventListener("touchmove", this.handleGlobalTouchMove, {
            passive: false,
            capture: true,
          });
          window.removeEventListener("touchend", this.handleGlobalTouchEnd, {
            capture: true,
          });
          if (this.props.onEndResize) {
            this.props.onEndResize(sectionIndex);
          }
        },
      });
    }

    // Bind global touch handlers
    this.handleGlobalTouchMove = (e) => {
      if (this.state.isDragging && e.touches.length === 1) {
        e.preventDefault();
        const y = e.touches[0].clientY + window.scrollY;
        const delta = y - this.startWindowY;
        if (this.props.onResize) {
          this.props.onResize(this.props.sectionIndex, delta, y);
        }
      }
    };
    this.handleGlobalTouchEnd = () => {
      if (this.state.isDragging) {
        window.chartResizeInProgress = false;
        this.setState({ isDragging: false, isHovered: false });
        window.removeEventListener("touchmove", this.handleGlobalTouchMove, {
          passive: false,
          capture: true,
        });
        window.removeEventListener("touchend", this.handleGlobalTouchEnd, {
          capture: true,
        });
        if (this.props.onEndResize) {
          this.props.onEndResize(this.props.sectionIndex);
        }
      }
    };
  }

  componentWillUnmount() {
    // Cleanup listeners (must match add options) — both the main document and
    // whichever popout document a drag may have been running in
    const docs = new Set([document, this._dragDoc].filter(Boolean));
    for (const doc of docs) {
      doc.removeEventListener("pointermove", this.handlePointerMove);
      doc.removeEventListener("pointerup", this.handlePointerUp);
      doc.removeEventListener("pointercancel", this.handlePointerUp);
    }
    const views = new Set([window, this._dragView].filter(Boolean));
    for (const view of views) {
      view.removeEventListener("touchmove", this.handleTouchDrag, {
        passive: false,
        capture: true,
      });
      view.removeEventListener("touchend", this.handleTouchDragEnd, {
        capture: true,
      });
      view.removeEventListener("touchcancel", this.handleTouchDragEnd, {
        capture: true,
      });
    }
    window.removeEventListener("touchmove", this.handleGlobalTouchMove, {
      passive: false,
      capture: true,
    });
    window.removeEventListener("touchend", this.handleGlobalTouchEnd, {
      capture: true,
    });
    document.body.style.cursor = "";

    // Unregister from global handlers
    const registryKey = this.getRegistryKey();
    if (window.__chartResizeHandlers) {
      window.__chartResizeHandlers.delete(registryKey);
    }

    // Clear global resize flag if component unmounts during drag
    if (this.state.isDragging && window.chartResizeInProgress) {
      window.chartResizeInProgress = false;
    }
  }

  renderSVG({ chartConfig }) {
    const { width, height } = chartConfig;
    const { disabled } = this.props;

    if (disabled || height <= 0) {
      return null;
    }

    // Position the handle at the bottom of the chart section
    const yPosition = Math.max(25, height - 15 + 16);

    // Theme-aware colors
    const isDark = isDarkTheme(useThemeStore.getState().theme);
    const baseColor = isDark ? "#424b57" : "#c8ccd0";
    const gutterColor = "transparent"; // Invisible touch target

    // Estados visuais para hover e drag - mudanças mais visíveis
    let handleColor = baseColor;
    let handleOpacity = 1.0;
    let highlightOpacity = 0.0; // Começa invisível
    let centerLineOpacity = 0.3;
    let dotsOpacity = 0.4;

    if (this.state.isDragging) {
      // Estado de arrastar - muito visível
      handleOpacity = 1.0;
      highlightOpacity = 0.6; // Bem visível
      centerLineOpacity = 0.9;
      dotsOpacity = 1.0;
    } else if (this.state.isHovered) {
      // Estado de hover - mudança bem visível
      handleOpacity = 1.0;
      highlightOpacity = 0.4; // Aparece no hover
      centerLineOpacity = 0.7;
      dotsOpacity = 0.8;
    }

    // Accent color for highlights (white on dark, dark on light)
    const accentColor = isDark ? "rgba(255, 255, 255, 1)" : "rgba(0, 0, 0, 1)";

    const barHeight = 4;

    // Dimensões seguindo o padrão PrimeReact - largura total do chart
    const handleWidth = width; // Toda a largura do chart
    const centerX = width / 2;

    // Touch target for resize interaction (reduced to minimize visual overlap)
    const touchTargetHeight = 20;

    return (
      <g className="chart-resize-handle">
        {/* Gutter area - larger touch target for touch devices */}
        <rect
          x={0}
          y={yPosition - touchTargetHeight / 2}
          width={width}
          height={touchTargetHeight}
          fill={gutterColor}
          style={{
            cursor: "ns-resize",
            pointerEvents: "all",
            touchAction: "none", // Prevent browser scroll during resize
          }}
          onPointerDown={this.handlePointerDown}
          onPointerEnter={this.handlePointerEnter}
          onPointerLeave={this.handlePointerLeave}
          onTouchStart={this.handleTouchStart}
          onTouchMove={this.handleTouchMove}
          onTouchEnd={this.handleTouchEnd}
        />

        {/* Container para todos os elementos visuais - move tudo junto */}
        <g>
          {/* Handle - retângulo ocupando toda a largura do chart */}
          <rect
            x={0}
            y={yPosition - barHeight / 2}
            width={handleWidth}
            height={barHeight}
            fill={handleColor}
            opacity={handleOpacity}
            rx={0} // Sem border-radius como no PrimeReact
            style={{
              cursor: "ns-resize",
              pointerEvents: "all",
              touchAction: "none", // Prevent browser scroll during resize
              transition: "opacity 0.15s ease", // Consistent with other elements
            }}
            onPointerDown={this.handlePointerDown}
            onPointerEnter={this.handlePointerEnter}
            onPointerLeave={this.handlePointerLeave}
            onTouchStart={this.handleTouchStart}
            onTouchMove={this.handleTouchMove}
            onTouchEnd={this.handleTouchEnd}
          />

          {/* Highlight superior - linha brilhante que aparece no hover */}
          {highlightOpacity > 0 && (
            <rect
              x={0}
              y={yPosition - barHeight / 2}
              width={handleWidth}
              height={1}
              fill={accentColor}
              opacity={highlightOpacity}
              style={{
                pointerEvents: "none",
                transition: "opacity 0.15s ease",
              }}
            />
          )}

          {/* Indicador visual central - pequena linha para mostrar o centro */}
          <rect
            x={centerX - 12}
            y={yPosition - 1}
            width={24}
            height={2}
            fill={accentColor}
            opacity={centerLineOpacity}
            rx={1}
            style={{
              pointerEvents: "none",
              transition: "opacity 0.15s ease",
            }}
          />

          {/* Grip dots para melhor indicação visual */}
          <circle
            cx={centerX - 6}
            cy={yPosition}
            r={0.5}
            fill={accentColor}
            opacity={dotsOpacity}
            style={{
              pointerEvents: "none",
              transition: "opacity 0.15s ease",
            }}
          />
          <circle
            cx={centerX}
            cy={yPosition}
            r={0.5}
            fill={accentColor}
            opacity={dotsOpacity}
            style={{
              pointerEvents: "none",
              transition: "opacity 0.15s ease",
            }}
          />
          <circle
            cx={centerX + 6}
            cy={yPosition}
            r={0.5}
            fill={accentColor}
            opacity={dotsOpacity}
            style={{
              pointerEvents: "none",
              transition: "opacity 0.15s ease",
            }}
          />
        </g>
      </g>
    );
  }

  render() {
    return (
      <GenericChartComponent
        svgDraw={this.renderSVG}
        drawOn={["pan"]}
        clip={false}
      />
    );
  }
}

ChartResizeHandle.propTypes = {
  chartId: PropTypes.string,
  sectionIndex: PropTypes.number.isRequired,
  disabled: PropTypes.bool,
  onStartResize: PropTypes.func,
  onResize: PropTypes.func,
  onEndResize: PropTypes.func,
};

ChartResizeHandle.defaultProps = {
  disabled: false,
  onStartResize: null,
  onResize: null,
  onEndResize: null,
};

export default ChartResizeHandle;
